import { z } from 'zod';
import { e164Schema } from './schemas.js';
/**
 * The sales pipeline: companies the sales team calls, with the people there
 * they talk to, on a board by where each stands. Everyone adds their own;
 * managers export them. A lead's calls are found by its people's numbers, and
 * they move it: a call out that nobody answers puts a lead still to call
 * under "No answer"; what came of a conversation the caller sets. Each lead
 * may say where it came from (Facebook, a cold call...), in the team's own
 * words, to see those leads only.
 */
export const leadStages = ['NEW', 'NO_ANSWER', 'NOT_INTERESTED', 'INTERESTED', 'FOLLOW_UP', 'DONE', 'CANCELLED'];
export const leadStageNames = {
    NEW: 'To call',
    NO_ANSWER: 'No answer',
    NOT_INTERESTED: 'Not interested',
    INTERESTED: 'Interested',
    FOLLOW_UP: 'Follow up',
    DONE: 'Done',
    CANCELLED: 'Cancelled',
};
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .default(null)
    .transform((v) => v || null);
/** A day, as YYYY-MM-DD. */
const daySchema = z.iso.date('Pick a date');
/** A time of day, as HH:MM. */
const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');
/** Someone at the company: the owner, the accountant... A number is one person's only. */
export const leadContactSchema = z
    .object({
    name: optionalText(120),
    position: optionalText(80),
    e164: e164Schema.nullable().default(null),
    email: z
        .union([z.literal(''), z.email('Not an email address')])
        .nullable()
        .default(null)
        .transform((v) => v || null),
})
    .refine((person) => person.name || person.e164 || person.email, { message: 'Give a name, number or email', path: ['name'] });
export const leadSchema = z
    .object({
    company: optionalText(160),
    /** The first is whom the card shows and calls. */
    contacts: z
        .array(leadContactSchema)
        .max(20)
        .default([])
        .superRefine((people, ctx) => {
        const seen = new Set();
        people.forEach((person, i) => {
            if (!person.e164)
                return;
            if (seen.has(person.e164))
                ctx.addIssue({ code: 'custom', message: 'A number is in the list twice', path: [i, 'e164'] });
            seen.add(person.e164);
        });
    }),
    activity: optionalText(120),
    topic: optionalText(120),
    /** Where the lead came from, e.g. "Facebook" or "Cold call". */
    source: optionalText(60),
    notes: z.string().max(10_000).default(''),
    ownerId: z.uuid().nullable().default(null),
})
    .refine((lead) => lead.company || lead.contacts.some((person) => person.name), { message: 'Give a company or a name', path: ['company'] });
/**
 * Moves a lead on the board, or sets when to call next or who works it. Only
 * what's given changes: a new day keeps the time; no day, no time.
 */
export const leadMoveSchema = z.object({
    stage: z.enum(leadStages).optional(),
    followUpOn: daySchema.nullable().optional(),
    /** At what time on that day (HH:MM); null for any time. */
    followUpTime: timeSchema.nullable().optional(),
    ownerId: z.uuid().nullable().optional(),
});
